import { Map } from "maplibre-gl";
import { bbox } from "@turf/turf";
import { isArray } from "lodash";


class AirSageMap extends Map {
  constructor(container, {
    style = "https://api.maptiler.com/maps/daf8b0da-58d2-4f37-9563-1f59182f279b/style.json?key=Pt2TXgCtJ5ziGcCMTIfn",
    darkStyle = "https://api.maptiler.com/maps/aa81da81-074e-4a48-9af9-066594208283/style.json?key=Pt2TXgCtJ5ziGcCMTIfn",
    preserveDrawingBuffer = false,
    eventHandlers = {},
    minZoom = 2,
    maxZoom = 18
  } = {}) {
    super({
      container,
      center: [-90.5794797, 39.8283459],
      zoom: 3.39,
      interactive: true,
      attributionControl: false,
      preserveDrawingBuffer,
      style: window.matchMedia("(prefers-color-scheme: dark)").matches ? darkStyle : style,
      minZoom,
      maxZoom
    });

    Object.entries(eventHandlers).forEach((e) => {
      const [event, handler] = e;
      this.on(event, handler);
    });
  }

  centerOn(feature, { padding = 15, easing } = {}) {
    const options = {
      padding
    };
    if (easing) {
      options.easing = easing;
    }
    if (isArray(feature)) {
      this.fitBounds(feature, options);
    } else {
      this.fitBounds(bbox(feature), options);
    }
  }

  /**
   * Register one arrowhead icon per key, drawn pointing north so `icon-rotate`
   * can take a bearing directly. The tip sits at the exact centre of the image
   * (the body fills the lower half) so that with `icon-anchor: center` the tip
   * stays pinned to the end of a line regardless of rotation.
   * @param {Object} colors map of key -> CSS color
   * @param {Object} [options]
   * @param {String} [options.prefix="arrowhead"] image id prefix, ids are `${prefix}-${key}`
   * @param {Number} [options.size=16] arrowhead height in CSS pixels
   * @param {String} [options.outlineColor] stroke colour around the head
   * @param {Number} [options.outlineWidth=1] stroke width in CSS pixels
   * @return {Object} map of key -> image id
   */
  addArrowheadImages(colors, { prefix = "arrowhead", size = 16, outlineColor = "rgba(0, 0, 0, 0.45)", outlineWidth = 1 } = {}) {
    const ids = {};
    Object.entries(colors).forEach(([key, color]) => {
      const id = `${prefix}-${key}`;
      ids[key] = id;
      if (!this.hasImage(id)) {
        this.addImage(id, drawArrowhead(color, size, { outlineColor, outlineWidth }), { pixelRatio: ARROWHEAD_PIXEL_RATIO });
      }
    });
    return ids;
  }
}

const ARROWHEAD_PIXEL_RATIO = 2;

/**
 * Draw an upward arrowhead of the given height (CSS px) on a square canvas twice
 * that size, with the tip at the canvas centre. Rendered at ARROWHEAD_PIXEL_RATIO.
 */
function drawArrowhead(color, heightCss, { outlineColor, outlineWidth }) {
  const ratio = ARROWHEAD_PIXEL_RATIO;
  const height = heightCss * ratio;
  const size = height * 2;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const cx = size / 2;
  const tipY = size / 2;
  // leave room for the outline so a thick stroke is not clipped at the canvas edge
  const margin = Math.max(height * 0.1, outlineWidth * ratio);
  const baseY = size - margin;
  const halfWidth = height * 0.45;
  ctx.beginPath();
  ctx.moveTo(cx, tipY);
  ctx.lineTo(cx + halfWidth, baseY);
  ctx.lineTo(cx, baseY - height * 0.3);
  ctx.lineTo(cx - halfWidth, baseY);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = Math.max(1, outlineWidth * ratio);
  ctx.strokeStyle = outlineColor;
  ctx.lineJoin = "round";
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

export default AirSageMap;
